import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail } from "lucide-react";
import {
  AttendeeStatus,
  MemberPosition,
  MemberStatus,
  PaymentStatus,
} from "@satyrsmc/shared/lib/enums";
import type {
  EventAttendeeAdmin,
  EventAttendeeEmailAudienceType,
} from "@satyrsmc/shared/dto/admin/event";
import { trpc } from "@/trpc";
import { Button } from "@satyrsmc/shared/components";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { EventDescriptionEditor } from "./EventDescriptionEditor";

interface EmailAttendeesDialogProps {
  eventId: string;
  attendees: EventAttendeeAdmin[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Never email a cancelled attendee or one whose payment is mid-refund, regardless of audience. */
function isEligible(a: EventAttendeeAdmin): boolean {
  if (a.status === AttendeeStatus.No) return false;
  if (a.payment_status === PaymentStatus.RefundRequested) return false;
  if (a.payment_status === PaymentStatus.Refunded) return false;
  return true;
}

function matchesAudience(a: EventAttendeeAdmin, audience: EventAttendeeEmailAudienceType): boolean {
  if (!isEligible(a)) return false;
  const allowed =
    audience === "confirmed"
      ? [PaymentStatus.NotRequired, PaymentStatus.Confirmed]
      : [PaymentStatus.NotRequired, PaymentStatus.Confirmed, PaymentStatus.Pending];
  return !!a.payment_status && allowed.includes(a.payment_status);
}

export function EmailAttendeesDialog({
  eventId,
  attendees,
  open,
  onOpenChange,
}: EmailAttendeesDialogProps) {
  const [audience, setAudience] = useState<EventAttendeeEmailAudienceType>("confirmed_and_pending");
  const [fromMemberId, setFromMemberId] = useState<string | undefined>(undefined);
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const { confirm, confirmDialog } = useConfirm();

  const { data: members } = trpc.admin.members.list.useQuery(undefined, { enabled: open });

  const activeMembers = useMemo(() => {
    return (members ?? [])
      .filter((m) => m.status === MemberStatus.Active && m.email)
      .map((m) => ({ id: m.id, name: m.name, position: m.position ?? null }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [members]);

  // Default the "from" select to the reigning Correspondence Secretary, if one exists.
  const correspondenceSecretaryId = useMemo(
    () => activeMembers.find((m) => m.position === MemberPosition.CorrespondenceSecretary)?.id,
    [activeMembers],
  );
  const effectiveFromMemberId = fromMemberId ?? correspondenceSecretaryId;

  const recipientCount = useMemo(
    () => attendees.filter((a) => matchesAudience(a, audience)).length,
    [attendees, audience],
  );

  const emailAttendees = trpc.admin.events.emailAttendees.useMutation({
    onSuccess: ({ sent, skipped }) => {
      const base = `Emailed ${sent} attendee${sent === 1 ? "" : "s"}`;
      toast.success(skipped > 0 ? `${base} · ${skipped} skipped (no email)` : base);
      handleOpenChange(false);
    },
    onError: (err) => toast.error(err.message),
  });

  function handleOpenChange(next: boolean) {
    if (!next) {
      setAudience("confirmed_and_pending");
      setFromMemberId(undefined);
      setSubject("");
      setBodyHtml("");
    }
    onOpenChange(next);
  }

  const canSend =
    !emailAttendees.isPending &&
    !!effectiveFromMemberId &&
    subject.trim().length > 0 &&
    bodyHtml.trim().length > 0 &&
    recipientCount > 0;

  async function handleSend() {
    if (!effectiveFromMemberId) return;
    const ok = await confirm(
      `Send to ${recipientCount} attendee${recipientCount === 1 ? "" : "s"}?`,
    );
    if (!ok) return;
    emailAttendees.mutate({
      event_id: eventId,
      audience,
      from_member_id: effectiveFromMemberId,
      subject: subject.trim(),
      body_html: bodyHtml,
    });
  }

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Email Attendees</DialogTitle>
            <DialogDescription>
              Compose a one-off email to this event's attendees. Cancelled RSVPs and
              refunded/refund-requested attendees are never emailed.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Audience */}
            <div className="space-y-2">
              <p className="text-sm font-medium">Send to</p>
              <label className="flex items-start gap-3 rounded-md border p-3 cursor-pointer">
                <input
                  type="radio"
                  name="audience"
                  className="mt-0.5 size-4"
                  checked={audience === "confirmed_and_pending"}
                  onChange={() => setAudience("confirmed_and_pending")}
                />
                <span className="text-sm">
                  <span className="font-medium">All (Confirmed + Pending payment)</span>
                </span>
              </label>
              <label className="flex items-start gap-3 rounded-md border p-3 cursor-pointer">
                <input
                  type="radio"
                  name="audience"
                  className="mt-0.5 size-4"
                  checked={audience === "confirmed"}
                  onChange={() => setAudience("confirmed")}
                />
                <span className="text-sm">
                  <span className="font-medium">Confirmed Only</span>
                </span>
              </label>
              <p className="text-xs text-muted-foreground">
                This will email {recipientCount} attendee{recipientCount === 1 ? "" : "s"}.
              </p>
            </div>

            {/* From */}
            <div className="space-y-1.5">
              <p className="text-sm font-medium">From</p>
              <Select value={effectiveFromMemberId} onValueChange={setFromMemberId}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Choose a sender…" />
                </SelectTrigger>
                <SelectContent>
                  {activeMembers.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Subject */}
            <div className="space-y-1.5">
              <p className="text-sm font-medium">Subject</p>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Subject"
              />
            </div>

            {/* Body */}
            <div className="space-y-1.5">
              <p className="text-sm font-medium">Message</p>
              <EventDescriptionEditor
                value={bodyHtml}
                onChange={setBodyHtml}
                placeholder="Write your message..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSend} disabled={!canSend} className="gap-1.5">
              {emailAttendees.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Mail className="size-4" />
              )}
              {emailAttendees.isPending
                ? "Sending…"
                : `Send${recipientCount ? ` to ${recipientCount}` : ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {confirmDialog}
    </>
  );
}
